import { isDefined } from '@sgme/fp';
import type { LimitSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/limitSummaryMailRequest';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { useSPIntl } from 'hooks/useIntl';
import { useSelector } from 'react-redux';
import { limitOrderInputFormSelectors } from 'state/secondaryMarket/limit-order/form/input/inputForm.selectors';
import { limitOrderOriginalFormSelectors } from 'state/secondaryMarket/limit-order/form/original/original.selector';
import { limitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import { limitOrderTradeSelectors } from 'state/secondaryMarket/limit-order/order/order.selector';
import { limitOrderReferentialSelectors } from 'state/secondaryMarket/limit-order/referential/referential.selector';
import type { AppState } from 'state/state.model';

export const useLimitSummary = (tabId: string) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();

  const limitOrderId = useSelector((state: AppState) => limitOrderTradeSelectors.getOrderId(state, tabId));
  const limitClient = useSelector((state: AppState) => limitOrderSelectors.getFormClient(state, tabId));
  const limitClientType = useSelector((state: AppState) => limitOrderSelectors.getFormClientType(state, tabId));
  const limitOrderLimit = useSelector(
    (state: AppState) =>
      limitOrderInputFormSelectors.getLimit(state, tabId) ?? limitOrderOriginalFormSelectors.getLimit(state, tabId),
  );
  const limitCustodian = useSelector((state: AppState) => limitOrderSelectors.getFormCustodian(state, tabId));
  const limitQuantity = useSelector((state: AppState) => limitOrderInputFormSelectors.getFormQuantity(state, tabId));
  const limitPriceType = useSelector((state: AppState) => limitOrderOriginalFormSelectors.getPriceType(state, tabId));
  const limitSettlementDate = useSelector((state: AppState) =>
    limitOrderInputFormSelectors.getFormSettlementDate(state, tabId),
  );
  const limitCashAmount = useSelector((state: AppState) => limitOrderSelectors.getFormCashAmount(state, tabId));
  const limitCurrency = useSelector((state: AppState) => limitOrderReferentialSelectors.getCurrency(state, tabId));
  const limitIsin = useSelector((state: AppState) => limitOrderReferentialSelectors.getIsin(state, tabId));
  const limitFileCode = useSelector((state: AppState) => limitOrderReferentialSelectors.getFileCode(state, tabId));
  const limitRic = useSelector((state: AppState) => limitOrderReferentialSelectors.getRic(state, tabId));
  const limitTradeWay = useSelector((state: AppState) => limitOrderTradeSelectors.getTradeTradeWay(state, tabId));
  const limitSettlementUnit = useSelector((state: AppState) =>
    limitOrderInputFormSelectors.getFormTradeSizeInputMode(state, tabId),
  );
  const tradeWayLabel = useTradeWayLabel({ way: limitTradeWay });

  const limitSummaryMailRequestProps: LimitSummaryMailRequest = {
    limitOrderId: limitOrderId,
    limitClient:
      limitClientType !== 'Other'
        ? limitClientType
        : `${limitClient?.label} (${limitClient?.id} - ${limitClient?.eliotCode})`,
    limitOrderLimit: `${limitOrderLimit} %`,
    limitCustodian: `${limitCustodian?.label} (${limitCustodian?.id} - ${limitCustodian?.eliotCode})`,
    limitQuantity: limitQuantity,
    limitPriceType: limitPriceType,
    limitSettlementDate: formatDisplayedDate(limitSettlementDate),
    limitCashAmount: `${formatNumber(limitCashAmount!, { maximumFractionDigits: 4 })} ${limitPriceType === 'Percent' ? '%' : limitCurrency}`,
    limitCurrency: limitCurrency,
    limitIsin: limitIsin,
    limitFileCode: limitFileCode,
    limitRic: limitRic,
    limitTradeWay: tradeWayLabel,
    limitSettlementUnit: isDefined(limitSettlementUnit)
      ? limitSettlementUnit.charAt(0).toUpperCase() + limitSettlementUnit!.slice(1)
      : limitSettlementUnit,
  };
  return limitSummaryMailRequestProps;
};
